import { useNavigate } from 'react-router-dom'
import {
  Calendar,
  MessageSquare,
  Pill,
  CreditCard,
  ChevronRight,
  Clock,
  AlertCircle,
} from 'lucide-react'
import { Button } from '@components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/ui/card'
import { usePortalDashboard } from '@hooks/usePortal'
import { useClinicFormat } from '@context/ClinicTimezoneContext'
import { cn } from '@lib/utils'

const quickActions = [
  {
    name: 'Agendar cita',
    description: 'Reserva tu próxima visita',
    icon: Calendar,
    href: '/portal/appointments/schedule',
  },
  {
    name: 'Solicitar resurtido',
    description: 'Pide el resurtido de tu receta',
    icon: Pill,
    href: '/portal/prescriptions',
  },
  {
    name: 'Enviar mensaje',
    description: 'Manda un mensaje seguro',
    icon: MessageSquare,
    href: '/portal/messages',
  },
  {
    name: 'Ver cobros',
    description: 'Revisa tu saldo pendiente',
    icon: CreditCard,
    href: '/portal/billing',
  },
]

export function PortalDashboardPage() {
  const navigate = useNavigate()
  const { formatDate, formatTime } = useClinicFormat()
  const { data, isLoading } = usePortalDashboard()

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Inicio</h1>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="h-32 animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 w-24 bg-muted rounded"></div>
                <div className="h-8 w-16 bg-muted rounded mt-2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  const stats = data?.stats

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Inicio</h1>
          <p className="text-muted-foreground mt-1">
            Bienvenido de nuevo, {data?.user?.first_name}
          </p>
        </div>
      </div>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0">
            <div className="space-y-1">
              <CardDescription>Próximas citas</CardDescription>
              <CardTitle className="text-2xl font-bold">
                {stats?.upcoming_appointments_count ?? 0}
              </CardTitle>
            </div>
            <div className="h-10 w-10 rounded-lg bg-medical-100 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-medical-600" />
            </div>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0">
            <div className="space-y-1">
              <CardDescription>Mensajes sin leer</CardDescription>
              <CardTitle className="text-2xl font-bold">
                {stats?.unread_messages_count ?? 0}
              </CardTitle>
            </div>
            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <MessageSquare className="h-5 w-5 text-blue-600" />
            </div>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0">
            <div className="space-y-1">
              <CardDescription>Recetas activas</CardDescription>
              <CardTitle className="text-2xl font-bold">
                {stats?.pending_prescriptions_count ?? 0}
              </CardTitle>
            </div>
            <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Pill className="h-5 w-5 text-green-600" />
            </div>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0">
            <div className="space-y-1">
              <CardDescription>Saldo pendiente</CardDescription>
              <CardTitle className="text-2xl font-bold">
                ${stats?.outstanding_balance?.toFixed(2) ?? '0.00'}
              </CardTitle>
            </div>
            <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-amber-600" />
            </div>
          </CardHeader>
        </Card>
      </section>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-medical-600" />
              Próximas citas
            </CardTitle>
            <CardDescription>Tus visitas programadas</CardDescription>
          </CardHeader>
          <CardContent>
            {!data?.upcoming_appointments?.length ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No tienes próximas citas</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => navigate('/portal/appointments/schedule')}
                >
                  Agendar ahora
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {data.upcoming_appointments.slice(0, 3).map((appt) => (
                  <div
                    key={appt.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate('/portal/appointments')}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-medical-100 flex items-center justify-center">
                        <Calendar className="h-5 w-5 text-medical-600" />
                      </div>
                      <div>
                        <p className="font-medium">{appt.appointment_type}</p>
                        <p className="text-sm text-muted-foreground">
                          Dr. {appt.doctor_name}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {formatDate(appt.appointment_date)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatTime(appt.appointment_date)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Acciones rápidas</CardTitle>
            <CardDescription>Funciones más usadas</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {quickActions.map((action) => {
              const Icon = action.icon
              return (
                <button
                  key={action.name}
                  onClick={() => navigate(action.href)}
                  className={cn(
                    'w-full flex items-start gap-3 p-3 rounded-lg border transition-all',
                    'hover:bg-muted hover:border-medical-200'
                  )}
                >
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5 text-slate-600" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-semibold text-sm">{action.name}</p>
                    <p className="text-xs text-muted-foreground">{action.description}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground self-center" />
                </button>
              )
            })}
          </CardContent>
        </Card>
      </div>

      {(stats?.outstanding_balance || 0) > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="flex items-center gap-4 py-4">
            <AlertCircle className="h-6 w-6 text-amber-600" />
            <div className="flex-1">
              <p className="font-medium">Saldo pendiente</p>
              <p className="text-sm text-muted-foreground">
                Tienes ${stats?.outstanding_balance.toFixed(2)} pendientes. Realiza un pago para evitar cargos por atraso.
              </p>
            </div>
            <Button onClick={() => navigate('/portal/billing')}>Ver cobros</Button>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
